import { useCallback, useMemo, type ReactNode } from "react";
import classNames from "classnames";
import { CarouselHeader } from "src/molecules/CarouselHeader";
import { Carousel } from "src/molecules/Carousel";
import { CategoryCard, CategoryCardColor } from "src/molecules/CategoryCard";
import { locale } from "src/config/app-config";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { Category } from "src/types/Category.d";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { trackCarouselViewedEvent } from "src/tracking/events/carousel-viewed";
import { trackCarouselViewAllSelectedEvent } from "src/tracking/events/carousel-view-all-selected";
import { CAROUSEL_CONTENT_TYPE } from "src/utils/constants";
import { trackCarouselItemSelectedEvent } from "src/tracking/events/carousel-item-selected";
import { Grid } from "src/organisms/Grid";

import styles from "./CategoryCarousel.module.scss";

interface CategoryCarouselProps {
  title?: string | JSX.Element;
  subTitle?: string;
  headerLinkHref?: string;
  headerLinkAs?: string;
  categories?: Category[];
  color?: CategoryCardColor;
  genderId: string;
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  isDesigner?: boolean;
  carouselPosition: number;
  carouselId: string;
  isGated: boolean;
}

// TODO: HOTFIX UNTIL CONTENT MANAGERS MIGRATE EVERYTHING IN COMMERCETOOLS
// https://otrium.atlassian.net/browse/CN-1668

const getPlGenderId = (gender: string): string => {
  switch (gender) {
    case "women":
      return "kobiety";
    case "men":
      return "mezczyzni";
    case "kids":
      return "dzieci";
  }
  return "";
};

const checkUrl = (
  url: string,
  genderId: string,
  locale: string,
  isDesigner: boolean | undefined
) => {
  if (url) {
    if (locale === "pl-pl" && !url.includes("otrium.pl") && !isDesigner) {
      const engGender = localeFix({ gender: genderId });

      const [firstPartNewUrl, secondPartNewUrl] = url
        .replace(".nl", ".pl")
        .replace(".com", ".pl")
        .split(".pl");
      const [, productCategorie, , ...categorySlug] =
        secondPartNewUrl.split("/");
      const plGender = getPlGenderId(engGender);

      const newUrl = [
        firstPartNewUrl + ".pl",
        productCategorie,
        plGender,
        categorySlug[categorySlug.length - 2],
      ].join("/");

      return newUrl;
    } else {
      return url;
    }
  }

  return undefined;
};

function CategoryCarousel({
  title,
  headerLinkHref,
  subTitle,
  headerLinkAs,
  categories,
  color,
  genderId,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  isDesigner = false,
  carouselPosition,
  carouselId,
  isGated,
}: CategoryCarouselProps): JSX.Element | null {
  const { currentShopType } = useShopTypeContext();

  const trackingData = useMemo(() => {
    if (typeof title === "string" && categories?.length) {
      return {
        title,
        subtitle: subTitle,
        items: categories,
        shopType: currentShopType,
        contentType: CAROUSEL_CONTENT_TYPE.CATEGORY,
        carouselPosition,
        carouselId,
        gated: isGated,
      };
    }

    return undefined;
  }, [
    title,
    subTitle,
    categories,
    currentShopType,
    carouselPosition,
    carouselId,
    isGated,
  ]);

  const handleTrackNavigation = useCallback(
    (visibleIndexes: number[]) => {
      if (trackingData) {
        void trackCarouselViewedEvent({ ...trackingData, visibleIndexes });
      }
    },
    [trackingData]
  );

  const handleViewAllClick = useCallback(() => {
    if (trackingData) {
      void trackCarouselViewAllSelectedEvent({
        ...trackingData,
      });
    }
  }, [trackingData]);

  const handleCardClick = useCallback(
    (itemId: string) => {
      if (trackingData && categories && categories.length) {
        void trackCarouselItemSelectedEvent({
          ...trackingData,
          itemId,
        });
      }
    },
    [trackingData, categories]
  );

  const items = useMemo(
    () =>
      categories?.map<ReactNode>((category) => {
        const url = checkUrl(category.url, genderId, locale, isDesigner);

        return (
          <CategoryCard
            key={category.id}
            size="m"
            category={category}
            link={
              url
                ? url
                : isDesigner
                ? `${getI18nRoute({
                    route: `/${genderId}/designer`,
                    locale,
                  })}/${category.slug}`
                : `${getI18nRoute({
                    route: `/${genderId}`,
                    locale,
                  })}/${category.slug}`
            }
            color={color}
            hasPadding={false}
            onClick={() => handleCardClick(category.id)}
          />
        );
      }) || [],
    [categories, color, genderId, isDesigner, handleCardClick]
  );

  if (!categories || !categories?.length) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.track}>
        <CarouselHeader
          title={title}
          subTitle={subTitle}
          href={headerLinkHref}
          as={headerLinkAs}
          viewAllOnClick={handleViewAllClick}
          showViewAllDesktop
          bottomSpacing={["space8", "space8", "space8", "space16"]}
        />
      </div>
      <div className={classNames(styles.gridWrapper, styles.track)}>
        <Grid
          items={items}
          gap={{ mobile: "space8", tablet: "space8" }}
          itemsPerColumn={{
            mobile: 3,
            tablet: 3,
          }}
        />
      </div>

      <div className={classNames(styles.carouselWrapper, styles.track)}>
        <Carousel
          items={items}
          slidesDesktop={slidesDesktop}
          slidesTablet={slidesTablet}
          slidesMobile={slidesMobile}
          marginSize="s"
          trackCarouselViewed={handleTrackNavigation}
        />
      </div>
    </div>
  );
}

export default CategoryCarousel;
